import { reportBrowser as api } from 'st-app/api'
import { Accessor, createEffect, createSignal, For, Match, on, Setter, Show, Signal, Switch } from "solid-js"
import './report-browser.css'
import STInput from '../st-input/st-input'
import ButtonGroup from '../st-button-group/st-button-group'
import STButton, { ButtonType } from '../st-button/st-button'
import STModal from '../st-modal/st-modal'
import CreateReportWizard from './create-report-wizard'
import { ReportBrowserContext, ReportBrowserProvider, useReportBrowser, ViewMode } from './report-browser-context'
import { TileView } from './tile-view'
import { ListView } from './list-view'
import CarouselView from './carousel-view'
import PublishDialog from './publish-dialog'
import DeleteDialog from './delete-dialog'
import ReportThumbnailDialog from './report-thumbnail-dialog'
import CopyDialog from './copy-dialog'
import { createProxySignal } from 'common'

const Breadcrumbs = () => {
    const reportBrowser = useReportBrowser()!
    const navHome = () => {
        reportBrowser.toggleCarousel(false)
        reportBrowser.setSelectedGroup(null)

    }

    return (
        <div class="st-breadcrumbs">
            <div class="st-breadcrumb" onClick={navHome}>
                <span class="fa fa-home"></span>
                <span class="st-breadcrumb-text">Home</span>
            </div>
            <Show when={reportBrowser.selectedGroup()}>
                <div>
                    <span class="fa fa-chevron-right" style="font-size: 20pt;"></span>
                </div>
                <div class="st-breadcrumb">
                    <span class="st-breadcrumb-text">
                        {reportBrowser.selectedGroup()!.description}
                    </span>
                </div>
            </Show>
        </div>
    )
}

const Toolbar = () => {
    const reportBrowser = useReportBrowser()!

    const [isEditing, setIsEditing] = createProxySignal(() => reportBrowser.state.isEditing, (v) => reportBrowser.toggleEditMode(v))
    const [isCreating, setIsCreating] = createProxySignal<boolean>(() => reportBrowser.state.isCreating, v => reportBrowser.toggleCreateMode(v))
    const searchStrSignal = createProxySignal(() => reportBrowser.state.searchStr, (v) => reportBrowser.setSearchStr(v));

    return (
        <div class="report-browser-toolbar">
            <div style="display: flex; gap: 8px">

                <Breadcrumbs></Breadcrumbs>
                <ButtonGroup>
                    <STButton onClick={() => reportBrowser.setViewMode('list')} active={!reportBrowser.state.isInCarousel && reportBrowser.state.viewMode === 'list'}>
                        <span class="fa fa-list"></span>
                    </STButton>
                    <STButton onClick={() => reportBrowser.setViewMode('tile')} active={!reportBrowser.state.isInCarousel && reportBrowser.state.viewMode === 'tile'}>
                        <span class="fa fa-grip-horizontal"></span>
                    </STButton>
                    <STButton onClick={() => reportBrowser.toggleCarousel()} active={reportBrowser.state.isInCarousel}>
                        <span class="fa fa-expand"></span>
                    </STButton>
                    <Show when={reportBrowser.state.canEdit && isEditing()}>
                        <STButton onClick={() => setIsEditing(false)} type="primary">
                            Lock
                        </STButton>

                        <STButton enabled={reportBrowser.state.canAdd} onClick={() => setIsCreating(true)} type="success">
                            <div style="display: flex; gap: 6px; align-items: center; white-space: nowrap;">
                                <span class="fa fa-plus"></span>
                                New Report
                            </div>
                        </STButton>
                    </Show>
                    <Show when={reportBrowser.state.canEdit && !isEditing()}>
                        <STButton enabled={reportBrowser.state.canEdit} onClick={() => { setIsEditing(true) }}>
                            <div style="display: flex; gap: 6px;">
                                <span class="fa fa-pencil"></span>
                                Edit
                            </div>
                        </STButton>
                    </Show>
                </ButtonGroup>
            </div>


            <div style="flex-basis: 33%;">
                <STInput value={searchStrSignal} placeholder="Search..."/>
            </div>

            <STModal show={[isCreating, setIsCreating]}>
                <Show when={isCreating()}>
                    <CreateReportWizard></CreateReportWizard>
                </Show>
            </STModal>
        </div>
    )
}


export class ReportBrowserProps { }

export default function ReportBrowser(props: ReportBrowserProps) {
    return (
        <ReportBrowserProvider>
            <div class="report-browser st-ui-scrollbars">
                <Toolbar></Toolbar>
                <ReportBrowserContent></ReportBrowserContent>
                <CopyDialog></CopyDialog>
                <PublishDialog></PublishDialog>
                <DeleteDialog></DeleteDialog>
                <ReportThumbnailDialog></ReportThumbnailDialog>
            </div>
        </ReportBrowserProvider>
    )
}
ReportBrowser.defaultProps = new ReportBrowserProps();

export function ReportBrowserContent() {
    const reportBrowser = useReportBrowser()!

    const init = async () => {
        await reportBrowser.refresh();

        const params = new URLSearchParams(window.location.search)
        const identity = params.get('identity')
        if (identity) {
            for (const group of reportBrowser.state.groups) {
                const report = group.reports.find(r => r.reportGU === identity)
                if (report) {
                    reportBrowser.setSelectedGroup(group)
                    reportBrowser.expandReport(report)
                }
            }
        }

        const secInfo = await api.GetSecurityInfo()
        reportBrowser.setCanAdd(secInfo.canAdd)
        reportBrowser.setCanEdit(secInfo.canEdit)
        reportBrowser.setCanPublish(secInfo.canPublish)
        reportBrowser.setCanAccessMyReports(secInfo.canAccessMyReports)
    }
   
    init()

    return (
        <Switch>
            <Match when={reportBrowser.state.isLoadingGroups}>
                <div class='report-browser-loading'>
                    <div class='fa fa-spinner fa-spin' style='font-size: 3rem;'></div>
                    <div>
                        Loading reports...
                    </div>
                </div>
            </Match>
            <Match when={reportBrowser.state.isInCarousel}>
                <CarouselView></CarouselView>
            </Match>

            <Match when={reportBrowser.state.viewMode === 'list'}>
                <ListView></ListView>
            </Match>

            <Match when={reportBrowser.state.viewMode === 'tile'}>
                <TileView></TileView>
            </Match>

        </Switch>
    )
}
